/**
 * Select Block Renderer
 * Renders multiple choice selections using CheckboxGroup
 */

import React from "react";
import { CheckboxGroup } from "@strivelabs/ui-blocks";
import type { UIBlock, UIActionEvent } from "@/types/ui-blocks";

export interface SelectBlockRendererProps {
  block: UIBlock;
  onUIAction: (event: UIActionEvent) => void;
  /** When true, the component is disabled */
  readOnly?: boolean;
}

export function SelectBlockRenderer({ 
  block, 
  onUIAction,
  readOnly = false
}: SelectBlockRendererProps) {
  const [disabled, setDisabled] = React.useState(readOnly);
  const [selectedValues, setSelectedValues] = React.useState<string[]>(
    block.type === 'select' ? block.content.selectedValues || [] : []
  );

  if (block.type !== 'select') return null;

  const { content } = block;

  const handleApply = (values: string[]) => {
    if (disabled) return;

    // Map selected values to their corresponding labels for display
    const labels = values.map(value => {
      const option = content.options.find(opt => opt.value === value);
      return option?.label || value;
    });

    onUIAction({
      type: "ui_action",
      component: "select",
      label: content.label,
      state: { values, labels }
    });

    setDisabled(true);
  };

  const handleCancel = () => {
    if (disabled) return;
    // Reset to initial values
    setSelectedValues(content.selectedValues || []);
    setDisabled(true);
  };

  // Convert SelectOption[] to CheckboxOption[]
  const checkboxOptions = content.options.map(opt => ({
    value: opt.value,
    label: opt.label,
    description: opt.description,
    disabled: opt.disabled
  }));

  return (
    <CheckboxGroup
      options={checkboxOptions}
      selectedValues={selectedValues}
      onApply={handleApply}
      onCancel={handleCancel}
      onTempChange={setSelectedValues}
      label={content.label}
      description={content.description}
      disabled={disabled}
    />
  );
}
